// Global temporary storage for size selection (only for current page session)
window.fdTempSizeSelection = null;

class ColorSwatch extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.logger = createLogger(true, 'color-swatch', LOG_LEVEL);
    this.sectionId = this.getAttribute('data-section-id');
    this.namespace = `.color-swatch-${this.sectionId}`;
  }

  connectedCallback() {
    const href = this.getAttribute('href');
    const imageUrl = this.getAttribute('data-image');
    const isActive = this.hasAttribute('active');
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          cursor: pointer;
          margin: 4px;
        }
        
        .swatch {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-size: cover;
          background-position: center;
          border: 2px solid transparent;
          transition: border-color 0.2s ease;
        }
        
        :host([active]) .swatch {
          border-color: var(--colorTextBody, #000);
        }
      </style>
      <div class="swatch" style="background-image: url(${imageUrl})"></div>
    `;

    this.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!href) return;

      /*
        Farmers Defense PDP reliability:
        Always perform a real browser navigation when a product/color swatch
        is selected. Do not fetch and replace #MainContent in-place.

        The previous AJAX swap could leave the destination PDP's inline bundle
        scripts unexecuted, which is why Buy 2 / Buy 3 could become visible but
        unclickable after visiting a product using a different buy box.
      */
      window.location.assign(href);
    });
  }
}

customElements.define('color-swatch', ColorSwatch);

class CustomStyleOptions extends HTMLElement {
  constructor() {
    super();
    this.logger = createLogger(true, 'style-options', LOG_LEVEL);
    this.colorOptions = null;
    this.sectionId = this.getAttribute('data-section-id');
    this.namespace = `.style-options-${this.sectionId}`;
  }

  connectedCallback() {
    this.logger.info("Custom style options connected");
    
    // Find color options element
    this.colorOptions = this.querySelector('custom-color-options');
    if (!this.colorOptions) {
      this.logger.error("No color options element found");
      return;
    }
    
    // Listen for color options initialization
    this.colorOptions.addEventListener('color-options:initialized', this.handleColorOptionsInitialized.bind(this));
    
    // Listen for theme content updates
    document.addEventListener('theme:content:updated', this.handleContentUpdate.bind(this));
    
    // Setup event listeners for style options
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.logger.info("Custom style options disconnected");
    if (this.colorOptions) {
      this.colorOptions.removeEventListener('color-options:initialized', this.handleColorOptionsInitialized);
    }
    document.removeEventListener('theme:content:updated', this.handleContentUpdate);
  }

  handleContentUpdate() {
    this.logger.info("Theme content updated, reinitializing style options");
    this.setupEventListeners();
  }

  handleColorOptionsInitialized(event) {
    this.logger.info("Color options initialized event received");
    if (!this.colorOptions) {
      this.logger.error("No color options element available");
      return;
    }

    const activeSwatch = this.colorOptions.querySelector('color-swatch.tag--active');
    if (activeSwatch) {
      const styleType = activeSwatch.dataset.type;
      this.logger.info(`Found active swatch with style type: ${styleType}`);
      
      // Find and click the corresponding style option
      const styleOption = this.querySelector(`.custom-style-option[data-type="${styleType}"]`);
      if (styleOption) {
        this.logger.info(`Found matching style option, clicking it`);
        // Show color options before clicking
        this.colorOptions.show();
        styleOption.click();
      } else {
        this.logger.error(`No style option found for type: ${styleType}`);
      }
    } else {
      this.logger.info("No active swatch found");
    }
  }

  setupEventListeners() {
    // First check for direct children
    const directOptions = this.querySelectorAll(".custom-style-option");
    if (directOptions.length > 0) {
      this.logger.info(`Found ${directOptions.length} direct style options`);
      directOptions.forEach((el) => {
        el.addEventListener("click", this.handleOptionClick.bind(this));
      });
    }

    // Then check for elements within .product-block
    const productBlock = document.querySelector(".product-block");
    if (productBlock) {
      const blockOptions = productBlock.querySelectorAll(".custom-style-option");
      if (blockOptions.length > 0) {
        this.logger.info(`Found ${blockOptions.length} style options in product block`);
        blockOptions.forEach((el) => {
          el.addEventListener("click", this.handleOptionClick.bind(this));
        });
      }
    }

    if (directOptions.length === 0 && (!productBlock || productBlock.querySelectorAll(".custom-style-option").length === 0)) {
      this.logger.info("No custom style options found");
    }
  }

  handleOptionClick(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    
    const el = evt.currentTarget;
    const selectedValue = el.dataset.type;
    
    // Update active state
    const container = el.closest(".product-block") || this;
    container.querySelectorAll(".custom-style-option").forEach((ele) => {
      ele.classList.remove("tag--active");
    });
    el.classList.add("tag--active");

    // Save selection
    const pathSegments = window.location.pathname.split('/');
    const lastValue = pathSegments.pop();
    const sessionKeyName = `${lastValue}-style-option-selected`;
    sessionStorage.setItem(sessionKeyName, selectedValue);
    this.logger.info(`Saved selection: ${selectedValue}`);

    // Dispatch style option selected event
    document.dispatchEvent(new CustomEvent('style-option:selected', {
      detail: { 
        type: selectedValue,
        sectionId: this.sectionId
      },
      bubbles: true
    }));

    // Handle color swatches
    if (!this.colorOptions) {
      this.logger.error('No color options element available');
      return;
    }

    // Use the data-type value to match against swatch data-types
    const label = el.querySelector('.variant__button-label');
    const styleName = label ? label.textContent.trim() : selectedValue;
    this.logger.info(`Showing swatches for style: ${styleName}, selectedValue: ${selectedValue}`);
    this.logger.info(`Style option data-type: "${selectedValue}"`);
    // Show color options before showing matching swatches
    this.colorOptions.show();
    this.logger.info('Calling showMatchingSwatches with selectedValue:', selectedValue);
    this.colorOptions.showMatchingSwatches(selectedValue);
  }

  reinitializeProductSection(productSection) {
    this.logger.info('Reinitializing product section');
    
    // Initialize product handling
    const product = new theme.Product(productSection);
    product.formSetup();
    product.productSetup();
    product.videoSetup();
    product.initProductSlider();
    product.customMediaListners();
    
    // Skip recently viewed for quickview
    product.addIdToRecentlyViewed = () => {}; // Override to prevent storage quota error
    
    // Initialize collapsibles
    theme.collapsibles.init(productSection);
    
    // Initialize custom add-to-cart button
    const form = productSection.querySelector('.product-single__form');
    if (form) {
      this.logger.info('Found product form, initializing ATC button');
      new CustomAddToCartButton(productSection);
    }
    
    // Dispatch product section reinitialized event
    document.dispatchEvent(new CustomEvent('product-section:reinitialized', {
      detail: {
        productId: productSection.getAttribute('data-product-id')
      }
    }));
  }
}

customElements.define('custom-style-options', CustomStyleOptions);

class CustomColorOptions extends HTMLElement {
  constructor() {
    super();
    this.logger = createLogger(true, 'color-options', LOG_LEVEL);
    this.sectionId = this.getAttribute('data-section-id');
    this.namespace = `.color-options-${this.sectionId}`;
  }

  connectedCallback() {
    this.logger.info('Custom color options connected');
    
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.initialize.bind(this));
    } else {
      this.initialize();
    }

    // Listen for theme content updates
    document.addEventListener('theme:content:updated', this.handleContentUpdate.bind(this));
  }

  disconnectedCallback() {
    this.logger.info('Custom color options disconnected');
    document.removeEventListener('theme:content:updated', this.handleContentUpdate);
  }

  handleContentUpdate() {
    this.logger.info('Theme content updated, reinitializing color options');
    this.initialize();
  }

  initialize() {
    this.logger.info('Initializing color options');
    
    // Check if there's an active swatch
    const activeSwatch = this.querySelector('color-swatch.tag--active');
    if (activeSwatch) {
      this.logger.info('Found active swatch on initialization');
      this.showMatchingSwatches(activeSwatch.dataset.type);
      // Ensure we're visible after initialization
      this.classList.remove('hide');
    } else {
      this.logger.info('No active swatch found on initialization');

      // Check if we have swatches without data-type (collection template)
      const swatches = this.querySelectorAll('color-swatch');
      const swatchesWithType = Array.from(swatches).filter(el => el.dataset.type);

      if (swatches.length > 0 && swatchesWithType.length === 0) {
        this.logger.info('Found swatches without data-type, showing all (collection template)');
        swatches.forEach(el => el.classList.remove('hide'));
        this.classList.remove('hide');
      } else if (swatches.length === 0) {
        this.classList.add('hide');
      } else if (swatchesWithType.length > 0) {
        // We have swatches with data-types but no active one, click the first style option
        this.logger.info('Found swatches with data-types but no active swatch, clicking first style option');
        const firstStyleOption = document.querySelector('.custom-style-option');
        if (firstStyleOption) {
          this.logger.info('Clicking first style option to activate swatches');
          firstStyleOption.click();
        } else {
          this.logger.error('No style options found to click');
        }
      }
    }
    
    // Listen for style option changes
    document.addEventListener('style-option:selected', (event) => {
      this.logger.info('Style option selected event received:', event.detail);
      if (event.detail && event.detail.type) {
        this.showMatchingSwatches(event.detail.type);
        // Ensure we're visible after style selection
        this.classList.remove('hide');
      }
    });

    // Dispatch initialization event
    this.dispatchEvent(new CustomEvent('color-options:initialized', {
      bubbles: true,
      detail: { 
        element: this,
        sectionId: this.sectionId
      }
    }));
  }

  showMatchingSwatches(type) {
    this.logger.info(`Showing color swatches for type: ${type}`);

    // Find all swatches within this custom element
    const swatches = this.querySelectorAll('color-swatch');
    this.logger.info(`Found ${swatches.length} total swatches`);

    // Check if any swatches have data-type attributes
    const swatchesWithType = Array.from(swatches).filter(el => el.dataset.type);

    // If no swatches have data-type (collection template), show all swatches
    if (swatchesWithType.length === 0) {
      this.logger.info('No swatches have data-type attribute, showing all swatches (collection template)');
      swatches.forEach(el => {
        el.classList.remove('hide');
      });
      this.classList.remove('hide');
      return;
    }

    // Hide all swatches first
    swatches.forEach(el => {
      el.classList.add('hide');
    });

    // Helper function to normalize strings for comparison
    const normalizeForComparison = (str) => {
      if (!str) return '';
      return str.toLowerCase()
        .replace(/[^\w]/g, '') // Remove all non-alphanumeric characters
        .trim();
    };

    const normalizedType = normalizeForComparison(type);
    this.logger.info(`Normalized search type: "${type}" -> "${normalizedType}"`);

    // Show only matching swatches - using normalized comparison
    const matchingSwatches = Array.from(swatches).filter(el => {
      const swatchType = el.dataset.type;
      const normalizedSwatchType = normalizeForComparison(swatchType);
      this.logger.debug(`Checking swatch type: "${swatchType}" (normalized: "${normalizedSwatchType}") against "${normalizedType}"`);
      return normalizedSwatchType === normalizedType;
    });
    
    this.logger.info(`Found ${matchingSwatches.length} matching swatches for type: ${type}`);
    
    matchingSwatches.forEach(el => {
      el.classList.remove('hide');
      this.logger.debug(`Showing swatch: ${el.dataset.type}`);
    });

    // Always show the container if we have matching swatches
    if (matchingSwatches.length > 0) {
      this.logger.info('Showing color options container');
      this.classList.remove('hide');
    } else {
      this.logger.info('Hiding color options container - no matching swatches');
      this.classList.add('hide');
    }
  }

  show() {
    this.logger.info('Showing color options container');
    this.classList.remove('hide');
  }

  hide() {
    this.logger.info('Hiding color options container');
    this.classList.add('hide');
  }
}

customElements.define('custom-color-options', CustomColorOptions);

class CartRemove extends HTMLElement {
  constructor() {
    super();
    this.logger = createLogger(true, 'cart-remove', LOG_LEVEL);
    this.sectionId = this.getAttribute('data-section-id');
    this.namespace = `.cart-remove-${this.sectionId}`;
    this.handleClick = this.handleClick.bind(this);
    this.init();
  }

  init() {
    this.logger.info("Initializing cart remove element");
    // Remove any existing click handlers
    this.removeEventListener('click', this.handleClick);
    this.addEventListener('click', this.handleClick);
    this.setAttribute('data-fd-cart-remove-initialized', 'true');
    this.classList.add('fd-cart-remove-initialized');
    
    this.logger.info('Cart remove element initialized', {
      element: this,
      hasClass: this.classList.contains('fd-cart-remove-initialized'),
      attributes: Array.from(this.attributes).map(attr => `${attr.name}=${attr.value}`)
    });
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.logger.info('Cart remove clicked', { element: this });
    
    const link = this.querySelector('a');
    if (!link) {
      this.logger.error('No remove link found in cart-remove element');
      return;
    }

    try {
      // Show loading state
      this.classList.add('cart-remove--in-progress');
      
      const url = new URL(link.href);
      const id = url.searchParams.get('id');
      
      if (!id) {
        this.logger.error('No item ID found in remove link');
        return;
      }

      this.logger.info(`Removing item ${id} from cart`);
      theme.cart.changeItem(id, 0).then(cart => {
        // Dispatch cart updated event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart }
        }));
        
        // Trigger cart build to refresh items
        document.dispatchEvent(new CustomEvent('cart:build'));
      }).catch(error => {
        this.logger.error('Error handling cart remove:', error);
      }).finally(() => {
        this.classList.remove('cart-remove--in-progress');
      });
    } catch (error) {
      this.logger.error('Error handling cart remove:', error);
      this.classList.remove('cart-remove--in-progress');
    }
  }
}

if (!customElements.get('cart-remove')) {
  customElements.define('cart-remove', CartRemove);
}

// Handle dynamically added cart items
const cartObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length) {
      mutation.addedNodes.forEach((node) => {
        if (node.querySelectorAll) {
          node.querySelectorAll('cart-remove').forEach(removeEl => {
            // Skip if already initialized or if init doesn't exist (useTieredProducts is false)
            if (removeEl.hasAttribute('data-fd-cart-remove-initialized') || typeof removeEl.init !== 'function') {
              return;
            }
            // Reinitialize the element
            removeEl.init();
          });
        }
      });
    }
  });
});

// Start observing all possible cart containers
const cartContainers = [
  // Drawer cart
  document.querySelector('#CartDrawerForm .drawer__scrollable'),
  // Popup cart
  document.querySelector('#CartPopup .drawer__scrollable'),
  // Regular cart page
  document.querySelector('.cart__page .cart__items')
].filter(Boolean); // Remove null/undefined containers

if (cartContainers.length > 0) {
  cartContainers.forEach(container => {
    cartObserver.observe(container, {
      childList: true,
      subtree: true
    });
    
    // Initialize any existing cart-remove elements (only if CartRemove class is registered)
    container.querySelectorAll('cart-remove').forEach(removeEl => {
      if (!removeEl.hasAttribute('data-fd-cart-remove-initialized') && typeof removeEl.init === 'function') {
        removeEl.init();
      }
    });
  });
}

class CustomAddToCartButton {
  constructor(container) {
    this.logger = createLogger(true, 'atc-button', LOG_LEVEL);
    this.container = container;
    this.colorSwatchData = [];
    this.sectionId = container.getAttribute('data-section-id');
    this.namespace = `.atc-button-${this.sectionId}`;
    this.init();
  }

  init() {
    this.logger.info('Initializing ATC button', this.container);

    // Check if product title contains "Sleeves", "Gloves", "Hoodie", or "Apron"
    const productTitle = this.container.querySelector('.product-single__title');
    const titleText = productTitle ? productTitle.textContent.toLowerCase() : '';
    const hasRequiredProduct = titleText.includes('sleeve') || titleText.includes('glove') || titleText.includes('hoodie') || titleText.includes('apron');
    
    if (!productTitle || !hasRequiredProduct) {
      this.logger.info('Product title does not contain "Sleeve", "Glove", "Hoodie", or "Apron", skipping ATC button and color swatch initialization');
      return;
    }

    // Check if style-wrapper-custom exists
    const requiresCustomATCButton = this.container.querySelector('fieldset[data-custom-atc-button]');
    if (!requiresCustomATCButton) {
      this.logger.info('No custom style options found, skipping ATC button and color swatch initialization');
      return;
    }

    // Store all color swatches and their variant data
    const colorSwatches = this.container.querySelectorAll('custom-color-options color-swatch, .cso-rows-block color-swatch');
    this.logger.info(`Found ${colorSwatches.length} color swatches`);

    // Process color swatch data
    this.colorSwatchData = Array.from(colorSwatches).map(swatch => {
      try {
        // Find the script tag with variant data - it's the next sibling element
        const variantScript = swatch.nextElementSibling;
        
        if (!variantScript || !variantScript.classList.contains('variant-data')) {
          this.logger.error('No variant data found for swatch');
          return {
            element: swatch,
            variants: []
          };
        }

        const variants = JSON.parse(variantScript.textContent);
        this.logger.success(`Successfully parsed variants for color swatch: ${variants.length} variants`);
        return {
          element: swatch,
          variants: variants
        };
      } catch (e) {
        this.logger.error('Failed to parse variants data for a color swatch:', e);
        return {
          element: swatch,
          variants: []
        };
      }
    });

    // Get the form element and handle submission
    const productForm = this.container.querySelector('.product-single__form');
    if (productForm) {
      this.logger.info('Found product form');
      
      // Add form submit handler
      productForm.addEventListener('submit', this.handleFormSubmit.bind(this));
      
      // Only modify ATC button if custom style options exist
      if (requiresCustomATCButton) {
        this.logger.info('Found custom style options, modifying ATC button');
        
        // Find the original add to cart button
        const originalButton = this.container.querySelector('button[type="submit"][data-add-to-cart]');
        if (originalButton) {
          this.logger.info('Found original add to cart button');
          
          // Create validation message if it doesn't exist
          let validationMessage = this.container.querySelector('.size-validation-message');
          if (!validationMessage) {
            validationMessage = document.createElement('div');
            validationMessage.className = 'size-validation-message';
            validationMessage.style.display = 'none';
            validationMessage.textContent = 'Please select a size';
            this.logger.info('Created validation message element');
          }
          
          // Find the size inputs container
          const sizeInputsContainer = this.container.querySelectorAll('fieldset[data-custom-atc-button]');
          if (sizeInputsContainer) {
            // Insert validation message at the beginning of the size inputs container
            sizeInputsContainer.forEach(input => {
              input.insertBefore(validationMessage, input.firstChild);
            });
            this.logger.info('Inserted validation message before size inputs');
          } else {
            this.logger.error('Could not find size inputs container');
          }
          
          // Hide the original button
          originalButton.style.display = 'none';
          this.logger.debug('Hidden original button');
          
          // Remove any existing proxy buttons
          const existingProxyButtons = this.container.querySelectorAll('.fd-add-to-cart-btn');
          existingProxyButtons.forEach(button => {
            button.remove();
            this.logger.info('Removed existing proxy button');
          });
          
          // Create a proxy div with different classes
          const proxyButton = document.createElement('div');
          proxyButton.className = 'fd-add-to-cart-btn ' + originalButton.className;
          proxyButton.setAttribute('data-fd-proxy-button', 'true');
          proxyButton.innerHTML = originalButton.innerHTML;
          proxyButton.style.cursor = 'pointer';
          this.logger.debug('Created proxy div');
          
          // Add our own click handler to the proxy div
          proxyButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.logger.info('Proxy div clicked');
            
            if (!this.validateSize()) {
              this.logger.error('Size validation failed, preventing form submission');
              return;
            }

            // Show loading state on proxy button
            proxyButton.classList.add('is-loading');

            // Farmers Defense hoodie bundle:
            // The PDP popup already knows the exact selected Hoodie 2/3 variants.
            // For Buy 2 / Buy 3, bypass the normal form quantity submission
            // (which was adding 2x/3x of the main variant) and submit the exact
            // selected variant IDs as separate cart lines.
            const bundleRoot = this.container.querySelector('[data-fd-pdp-bundle]');
            const selectedOffer = bundleRoot?.querySelector('[data-fd-bundle-option].is-selected');
            const bundleQty = Number(selectedOffer?.dataset.qty || 1);

            if (bundleRoot && bundleQty > 1) {
              const mainVariantField = productForm.querySelector('[name="id"]');
              const mainVariantId = Number(mainVariantField?.value || 0);
              const items = [];

              if (!mainVariantId) {
                this.logger.error('Bundle add failed: main variant ID missing');
                proxyButton.classList.remove('is-loading');
                return;
              }

              items.push({
                id: mainVariantId,
                quantity: 1,
                properties: {
                  '_FD Hoodie Offer': 'Buy ' + bundleQty,
                  '_FD Bundle Position': '1 of ' + bundleQty
                }
              });

              for (let slot = 2; slot <= bundleQty; slot++) {
                const slotEl = bundleRoot.querySelector('[data-fd-extra-slot="' + slot + '"]');
                const variantId = Number(slotEl?.getAttribute('data-fd-selected-variant-id') || 0);

                if (!variantId) {
                  this.logger.error('Bundle add failed: Hoodie ' + slot + ' variant ID missing');
                  proxyButton.classList.remove('is-loading');
                  return;
                }

                items.push({
                  id: variantId,
                  quantity: 1,
                  properties: {
                    '_FD Hoodie Offer': 'Buy ' + bundleQty,
                    '_FD Bundle Position': slot + ' of ' + bundleQty
                  }
                });
              }

              this.logger.info('Adding exact hoodie bundle variants', items);

              fetch('/cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({ items })
              })
                .then(response => response.json().then(data => {
                  if (!response.ok) {
                    throw new Error(data.description || data.message || 'Could not add hoodie bundle');
                  }
                  return data;
                }))
                .then(response => {
                  document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                    bubbles: true,
                    detail: {
                      product: response,
                      addToCartBtn: originalButton
                    }
                  }));
                })
                .catch(error => {
                  this.logger.error('Error adding hoodie bundle:', error);
                  alert(error.message || 'Could not add hoodie bundle. Please try again.');
                })
                .finally(() => {
                  proxyButton.classList.remove('is-loading');
                });

              return;
            }

            // Non-bundle / Buy 1 behavior stays exactly as it was.
            if (theme.settings.enable_third_party_cart) {
              this.logger.info('Using third party cart, clicking original button');
              originalButton.click();
            } else {
              this.logger.info('Using theme cart, submitting form via AJAX');
              const ajaxProduct = new theme.AjaxProduct(productForm);
              ajaxProduct.addItemFromForm(e);
            }

            // Remove loading state when product is added or after timeout
            const removeLoading = () => proxyButton.classList.remove('is-loading');
            document.addEventListener('ajaxProduct:added', removeLoading, { once: true });
            setTimeout(removeLoading, 5000);
          });
          
          // Insert the proxy div before the original button
          originalButton.parentNode.insertBefore(proxyButton, originalButton);
          this.logger.success('Proxy div inserted into container');
        } else {
          this.logger.error('Could not find original add to cart button');
        }
      } else {
        this.logger.info('No custom style options found, leaving ATC button unchanged');
      }
      
      // Add event listeners for size changes and hover
      const sizeInputs = this.container.querySelectorAll('fieldset[data-custom-atc-button] input[name="Size"]');
      sizeInputs.forEach(input => {
        // Add change listener
        input.addEventListener('change', () => {
          this.logger.info(`Size selection changed to: ${input.value}`);
          
          // Save selected size to temporary global variable (only for current page session)
          window.fdTempSizeSelection = input.value;
          this.logger.info(`Saved temporary size selection: ${input.value}`);
          
          this.validateSize();
          this.updateColorSwatches(input.value);
        });

        // Find the associated label
        const label = input.nextElementSibling;
        if (!label) return;

        // Add hover listeners to the label
        label.addEventListener('mouseenter', () => {
          const selectedSize = input.value;
          this.logger.info(`Hovering over size: ${selectedSize}`);
          this.updateColorSwatches(selectedSize);
        });

        label.addEventListener('mouseleave', () => {
          this.logger.info('Mouse left size option, resetting to selected size');
          // Reset to the currently selected radio's value
          const selectedRadio = this.container.querySelector('fieldset[data-custom-atc-button] input[name="Size"]:checked');
          if (selectedRadio) {
            this.updateColorSwatches(selectedRadio.value);
          } else {
            this.resetColorSwatches();
          }
        });
      });

      // Initial state - don't disable the button
      const atcButton = this.container.querySelector('.fd-add-to-cart-btn');
      if (atcButton) {
        atcButton.disabled = false;
        atcButton.style.opacity = '1';
        atcButton.style.cursor = 'pointer';
        this.logger.info('ATC button enabled');
      }
    } else {
      this.logger.error('Product form not found');
    }
  }

  handleFormSubmit(e) {
    this.logger.info('Form submit intercepted');
    
    if (!this.validateSize()) {
      this.logger.error('Size validation failed, preventing form submission');
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // If validation passes, let theme.AjaxProduct handle the submission
    this.logger.info('Size validation passed, allowing form submission');
  }

  validateSize() {
    this.logger.info('Validating size...', this.container);

    const sizeInputsContainer = this.container.querySelectorAll('fieldset[data-custom-atc-button]');
    if (!sizeInputsContainer) {
      this.logger.error('No size inputs container found, passing');
      return true;
    }

    this.logger.info('Size inputs container found', sizeInputsContainer);

    let passed = true;
    sizeInputsContainer.forEach(input => {
      this.logger.info('Validating size...', input);
      const sizeInput = input.querySelector('input:checked');
      if (!sizeInput) {
        this.logger.error('No size selected');
        const validationMessage = this.container.querySelector('.size-validation-message');
        if (validationMessage) {
          validationMessage.style.display = 'block';
          this.logger.info('Showing validation message');
        } else {
          this.logger.error('Validation message element not found');
        }
        passed = false;
        return;
      }
      this.logger.success(`Size selected: ${sizeInput.value}`);
    });

    return passed;
  }

  // Function to update color swatches based on size
  updateColorSwatches(selectedSize) {
    this.logger.info('Updating color swatches', selectedSize, this.colorSwatchData);

    this.colorSwatchData.forEach(({element, variants}) => {
      const sizeVariant = variants.find(v => v.name === selectedSize);
      if (!sizeVariant || sizeVariant.inventory <= 0) {
        element.classList.add('disabled');
        element.classList.remove('enabled');
        // Add diagonal line overlay using CSS class
        const label = element.querySelector('.variant__button-label');
        if (label) {
          label.classList.add('out-of-stock');
        }
        this.logger.debug(`Color swatch has no inventory for size ${selectedSize}`);
      } else {
        element.classList.remove('disabled');
        element.classList.add('enabled');
        // Remove diagonal line class
        const label = element.querySelector('.variant__button-label');
        if (label) {
          label.classList.remove('out-of-stock');
        }
        this.logger.debug(`Color swatch has ${sizeVariant.inventory} units for size ${selectedSize}`);
      }
    });
  }

  // Function to reset all swatches to full opacity
  resetColorSwatches() {
    this.colorSwatchData.forEach(({element}) => {
      element.classList.remove('disabled');
      element.classList.add('enabled');
      // Remove diagonal line class
      const label = element.querySelector('.variant__button-label');
      if (label) {
        label.classList.remove('out-of-stock');
      }
    });
  }
}

class CustomQuickView {
  constructor() {
    this.logger = createLogger(true, 'quick-view', LOG_LEVEL);
    this.sectionId = document.querySelector('.modal--quick-shop')?.getAttribute('data-section-id') || 'global';
    this.namespace = `.quick-view-${this.sectionId}`;
    this.init();
  }

  init() {
    this.logger.info("Setting up quick view handling");
    
    // Remove any existing listeners to prevent duplicates
    document.removeEventListener('quickview:loaded', this.handleQuickViewLoaded.bind(this));
    document.removeEventListener('quickview:open', this.handleQuickViewOpen.bind(this));
    document.removeEventListener('modalClose', this.handleModalClose.bind(this));
    
    // Add the listeners
    document.addEventListener('quickview:loaded', this.handleQuickViewLoaded.bind(this));
    document.addEventListener('quickview:open', this.handleQuickViewOpen.bind(this));
    document.addEventListener('modalClose', this.handleModalClose.bind(this));
  }

  handleQuickViewOpen(event) {
    this.logger.info("Quick view open event received");
    const modal = document.querySelector('.modal--quick-shop.modal--is-active');
    if (modal) {
      this.logger.info("Found active modal");
      // Reinitialize everything since the modal content persists
      
      // Initialize style options
      const styleOptions = modal.querySelectorAll(".custom-style-option");
      if (styleOptions.length > 0) {
        this.initializeStyleOptions(styleOptions, modal);
        
        // Try to restore previous selection
        const pathSegments = window.location.pathname.split('/');
        const lastValue = pathSegments.pop();
        const sessionKeyName = `${lastValue}-style-option-selected`;
        const selectedValue = sessionStorage.getItem(sessionKeyName);
        
        if (selectedValue) {
          const selectedOption = modal.querySelector(`.custom-style-option[data-type="${selectedValue}"]`);
          if (selectedOption) {
            selectedOption.click();
          }
        } else {
          styleOptions[0].click();
        }
      }
    } else {
      this.logger.error("No active modal found");
    }
  }

  handleModalClose(event) {
    this.logger.info("Modal close event received");
    const modal = document.querySelector('.modal--quick-shop.modal--is-active');
    if (modal) {
      this.logger.info("Found active modal");
      // Just reset the active states without cleaning up event listeners
      modal.querySelectorAll('.custom-style-option').forEach(option => {
        option.classList.remove('tag--active');
      });
      
      const colorOptions = modal.querySelector('custom-color-options');
      if (colorOptions) {
        colorOptions.classList.add('hide');
      }

      const validationMessage = modal.querySelector('.size-validation-message');
      if (validationMessage) {
        validationMessage.style.display = 'none';
      }
    }
  }

  handleQuickViewLoaded(event) {
    this.logger.info("Quick view loaded event received");
    
    // Wait for modal content to be loaded
    setTimeout(() => {
      this.logger.info("Checking for custom style options in modal");
      const modal = document.querySelector('.modal--quick-shop.modal--is-active');
      if (modal) {
        this.logger.info("Found active modal element");
        
        // Add unique ID if not already present
        if (!modal.hasAttribute('data-fd-initialized')) {
          const uniqueId = `fd-modal-${Date.now()}`;
          modal.setAttribute('data-fd-initialized', uniqueId);
          this.logger.info(`Initializing new modal with ID: ${uniqueId}`);
          
          // Then initialize ATC button
          const form = modal.querySelector('.product-single__form');
          if (form) {
            this.logger.info("Found product form in modal, initializing ATC button");
            new CustomAddToCartButton(modal);
          } else {
            this.logger.error("No product form found in modal");
          }
          
          // Finally initialize style options
          const styleOptions = modal.querySelectorAll(".custom-style-option");
          this.logger.info(`Found style options in modal: ${styleOptions.length}`);
          
          if (styleOptions.length > 0) {
            this.logger.info("Initializing style options in modal");
            this.initializeStyleOptions(styleOptions, modal);
            
            // Try to restore previous selection
            const pathSegments = window.location.pathname.split('/');
            const lastValue = pathSegments.pop();
            const sessionKeyName = `${lastValue}-style-option-selected`;
            const selectedValue = sessionStorage.getItem(sessionKeyName);
            
            if (selectedValue) {
              this.logger.info(`Found previous selection: ${selectedValue}`);
              const selectedOption = modal.querySelector(`.custom-style-option[data-type="${selectedValue}"]`);
              if (selectedOption) {
                this.logger.info("Restoring previous selection");
                selectedOption.click();
              }
            } else {
              this.logger.info("No previous selection found, clicking first option");
              styleOptions[0].click();
            }
          } else {
            this.logger.error("No style options found in modal");
          }
        } else {
          this.logger.info("Modal already initialized, skipping initialization");
        }
      } else {
        this.logger.error("No active modal element found");
      }
    }, 100);
  }

  initializeStyleOptions(elements, container) {
    this.logger.info(`Initializing style options for ${elements.length} elements`);
    
    elements.forEach((el, index) => {
      this.logger.info(`Setting up style option ${index + 1}: ${el.dataset.type}`);
      
      // Remove any existing click listeners
      const newEl = el.cloneNode(true);
      el.parentNode.replaceChild(newEl, el);
      
      newEl.addEventListener("click", (evt) => {
        this.logger.info(`Style option clicked: ${newEl.dataset.type}`);
        evt.preventDefault();
        evt.stopPropagation();

        // Update active state
        container.querySelectorAll(".custom-style-option").forEach((ele) => {
          ele.classList.remove("tag--active");
        });
        newEl.classList.add("tag--active");

        // Save selection
        const pathSegments = window.location.pathname.split('/');
        const lastValue = pathSegments.pop();
        const sessionKeyName = `${lastValue}-style-option-selected`;
        const selectedValue = newEl.dataset.type;
        sessionStorage.setItem(sessionKeyName, selectedValue);
        this.logger.info(`Saved selection: ${selectedValue}`);

        // Handle color swatches using the new custom element
        const colorOptions = container.querySelector('custom-color-options');
        this.logger.info('Color options element:', colorOptions);
        if (colorOptions) {
          // Get the label text to match against the data-type
          const label = newEl.querySelector('.variant__button-label');
          const styleName = label ? label.textContent.trim() : selectedValue;
          this.logger.info(`Showing swatches for style: ${styleName}`);
          colorOptions.show();
          this.logger.info('Calling showMatchingSwatches');
          colorOptions.showMatchingSwatches(styleName);
        } else {
          this.logger.error('No color options element found');
        }
      });
    });
  }
}

class CustomElementsSection {
  constructor(container) {
    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');
    this.namespace = `.custom-elements-${this.sectionId}`;
    this.logger = createLogger(true, 'custom-elements-section', LOG_LEVEL);
    this.init();
  }

  init() {
    this.logger.info(`Initializing custom elements section ${this.sectionId}`);
    
    // Initialize cart remove functionality
    this.initializeCartRemove();
    
    // Initialize ATC button
    this.initializeATCButton();
    
    // Initialize quick view
    this.initializeQuickView();
    
    // Initialize custom style options
    this.initializeStyleOptions();

    // Listen for theme content updates
    document.addEventListener('theme:content:updated', this.handleContentUpdate.bind(this));
  }

  handleContentUpdate() {
    this.logger.info(`Theme content updated, reinitializing section ${this.sectionId}`);
    // Reinitialize all components
    this.init();
  }

  initializeCartRemove() {
    this.logger.info("Setting up cart remove functionality");

    // Handle dynamically added cart items
    const cartObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.querySelectorAll) {
              node.querySelectorAll('cart-remove').forEach(removeEl => {
                // Skip if already initialized
                if (removeEl.hasAttribute('data-fd-cart-remove-initialized')) {
                  this.logger.info("Cart remove element already initialized, skipping");
                  return;
                }

                this.logger.info("Found dynamically added cart-remove element");
                const link = removeEl.querySelector('a');
                if (link) {
                  this.logger.info("Found remove link, attaching click handler");
                  
                  // Create a bound version of handleClick for this element
                  const boundHandleClick = async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.logger.info('Cart remove clicked', { element: removeEl });
                    
                    try {
                      // Show loading state
                      removeEl.classList.add('cart-remove--in-progress');
                      
                      // Get the item ID from the link
                      const url = new URL(link.href);
                      const id = url.searchParams.get('id');
                      
                      if (!id) {
                        this.logger.error('No item ID found in remove link');
                        return;
                      }

                      this.logger.info(`Removing item ${id} from cart`);
                      const cart = await theme.cart.changeItem(id, 0);
                      
                      // Dispatch cart updated event
                      document.dispatchEvent(new CustomEvent('cart:updated', {
                        detail: { cart }
                      }));
                    } catch (error) {
                      this.logger.error('Error handling cart remove:', error);
                    } finally {
                      removeEl.classList.remove('cart-remove--in-progress');
                    }
                  };

                  // Remove any existing click handlers
                  link.removeEventListener('click', boundHandleClick);
                  link.addEventListener('click', boundHandleClick);
                  
                  // Mark as initialized
                  removeEl.setAttribute('data-fd-cart-remove-initialized', 'true');
                  removeEl.classList.add('fd-cart-remove-initialized');
                  
                  this.logger.info('Cart remove element initialized', {
                    element: removeEl,
                    hasClass: removeEl.classList.contains('fd-cart-remove-initialized'),
                    attributes: Array.from(removeEl.attributes).map(attr => `${attr.name}=${attr.value}`),
                    linkHref: link.href
                  });
                } else {
                  this.logger.error("No remove link found in cart-remove element");
                }
              });
            }
          });
        }
      });
    });

    // Start observing all possible cart containers
    const cartContainers = [
      // Drawer cart
      document.querySelector('#CartDrawerForm .drawer__scrollable'),
      // Popup cart
      document.querySelector('#CartPopup .drawer__scrollable'),
      // Regular cart page
      document.querySelector('.cart__page .cart__items')
    ].filter(Boolean); // Remove null/undefined containers

    if (cartContainers.length > 0) {
      this.logger.info(`Found ${cartContainers.length} cart container(s)`);
      cartContainers.forEach(container => {
        this.logger.info("Observing cart container for changes");
        cartObserver.observe(container, {
          childList: true,
          subtree: true
        });
        
        // Initialize any existing cart-remove elements
        container.querySelectorAll('cart-remove').forEach(removeEl => {
          if (!removeEl.hasAttribute('data-fd-cart-remove-initialized')) {
            const link = removeEl.querySelector('a');
            if (link) {
              const boundHandleClick = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.logger.info('Cart remove clicked', { element: removeEl });
                
                try {
                  removeEl.classList.add('cart-remove--in-progress');
                  const url = new URL(link.href);
                  const id = url.searchParams.get('id');
                  
                  if (!id) {
                    this.logger.error('No item ID found in remove link');
                    return;
                  }

                  this.logger.info(`Removing item ${id} from cart`);
                  const cart = await theme.cart.changeItem(id, 0);
                  
                  document.dispatchEvent(new CustomEvent('cart:updated', {
                    detail: { cart }
                  }));
                } catch (error) {
                  this.logger.error('Error handling cart remove:', error);
                } finally {
                  removeEl.classList.remove('cart-remove--in-progress');
                }
              };

              link.removeEventListener('click', boundHandleClick);
              link.addEventListener('click', boundHandleClick);
              removeEl.setAttribute('data-fd-cart-remove-initialized', 'true');
              removeEl.classList.add('fd-cart-remove-initialized');
              
              this.logger.info('Initialized existing cart-remove element', {
                element: removeEl,
                hasClass: removeEl.classList.contains('fd-cart-remove-initialized'),
                linkHref: link.href
              });
            }
          }
        });
      });
    } else {
      this.logger.info("No cart containers found");
    }
  }

  initializeATCButton() {
    this.logger.info('Initializing ATC button handler');
    
    // Initialize for main product page
    const mainContainer = document.querySelector('.product-single__meta');
    if (mainContainer) {
      this.logger.info('Found main product container, initializing ATC button');
      // Check if custom style options exist
      const requiresCustomATCButton = mainContainer.querySelector('fieldset[data-custom-atc-button]');
      if (requiresCustomATCButton) {
        this.logger.info('Found custom style options, initializing ATC button');
        new CustomAddToCartButton(mainContainer);
      } else {
        this.logger.info('No custom style options found, skipping ATC button initialization');
      }
    }
    
    // Initialize for quick view modal
    const modal = document.querySelector('.modal--quick-shop.modal--is-active');
    if (modal) {
      this.logger.info('Found quick view modal, initializing ATC button');
      // Check if custom style options exist
      const requiresCustomATCButton = modal.querySelector('fieldset[data-custom-atc-button]');
      if (requiresCustomATCButton) {
        this.logger.info('Found custom style options in modal, initializing ATC button');
        new CustomAddToCartButton(modal);
      } else {
        this.logger.info('No custom style options found in modal, skipping ATC button initialization');
      }
    }
  }

  initializeQuickView() {
    this.logger.info('Initializing quick view handler');
    new CustomQuickView();
  }

  initializeStyleOptions() {
    const existingStyleOptions = document.querySelectorAll(".custom-style-option");
    if (existingStyleOptions.length > 0) {
      this.logger.info(`Found existing style options on page: ${existingStyleOptions.length}`);
      const container = document.querySelector('.modal--quick-shop.modal--is-active') || document;
      const quickView = new CustomQuickView();
      quickView.initializeStyleOptions(existingStyleOptions, container);
    } else {
      this.logger.info("No existing style options found on page");
    }
  }

  onUnload() {
    this.logger.info(`Unloading custom elements section ${this.sectionId}`);
    document.removeEventListener('theme:content:updated', this.handleContentUpdate);
  }

  onSelect() {
    this.logger.info(`Selecting custom elements section ${this.sectionId}`);
  }

  onDeselect() {
    this.logger.info(`Deselecting custom elements section ${this.sectionId}`);
  }

  onBlockSelect(evt) {
    this.logger.info(`Block selected in custom elements section ${this.sectionId}: ${evt.detail.blockId}`);
  }

  onBlockDeselect(evt) {
    this.logger.info(`Block deselected in custom elements section ${this.sectionId}: ${evt.detail.blockId}`);
  }
}

// Register the section with theme.js
if (theme && theme.sections) {
  theme.sections.register('custom-elements', CustomElementsSection);
} else {
  // Fallback for non-Shopify contexts
  document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('[data-section-type="custom-elements"]');
    containers.forEach(container => {
      new CustomElementsSection(container);
    });
  });
}

// Initialize on DOM load for non-section contexts
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're in a section context
  const sectionContainer = document.querySelector('[data-section-type="custom-elements"]');
  if (!sectionContainer) {
    // If not in a section context, initialize directly
    new CustomElementsSection(document.body);
  }
});